import { memo, useCallback, useEffect, useRef, useState } from 'react'

import { Button, Text } from '@cash-me/react-components'
import NextImage from 'next/image'
import { useRouter } from 'next/router'

import { Grid, Col } from 'components/grid'
import { buildSimulatorUrl } from 'src/utils/buildSimulatorUrl'
import { pushDataLayer } from 'src/utils/push-data-layer'
import { theme } from 'styles/theme'

import { ANALYTICS_ACTION, ANALYTICS_CATEGORY } from '../ideal-credit/constants'

import * as S from './styles'

const TITLE = 'Veja como é fácil simular!'
const SUBTITLE = 'Com o Crédito com Garantia de Imóvel, você pode mais!'
const SIMULATE_GIF = `${process.env.NEXT_PUBLIC_SITE_IMAGES_PREFIX}/images/site/home/simulation/simulate.gif`

const Simulation = memo(() => {
  const router = useRouter()
  const gifRef = useRef<HTMLDivElement>(null)
  const [hasEnteredView, setHasEnteredView] = useState(false)

  useEffect(() => {
    const element = gifRef.current
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setHasEnteredView(true)
      },
      { threshold: 0.4 }
    )

    if (element) observer.observe(element)

    return () => {
      if (element) observer.unobserve(element)
    }
  }, [])

  const handleCtaClick = useCallback(() => {
    pushDataLayer({
      category: ANALYTICS_CATEGORY,
      action: ANALYTICS_ACTION,
      label: 'Crédito ideal - Simule agora'
    })

    location.href = buildSimulatorUrl(router.pathname, { medium: 'CreditoIdeal-SimularAgora' })
  }, [router.pathname])

  return (
    <S.Simulation>
      <Grid col={{ phone: 1, tablet: 1, desktop: 1 }}>
        <Col>
          <S.Content>
            <S.Container>
              <S.TopConnector aria-hidden>
                <S.ConnectorTriangle type="TriangleRound" draw="fill" color={theme.colors['cashme-magenta']} />
              </S.TopConnector>

              <S.Header>
                <Text as="h2" size="heading2" weight="medium" color="var(--colors-neutral-text-contrast-default)">
                  {TITLE}
                </Text>

                <Text as="p" size="heading4" weight="regular" color="var(--colors-neutral-text-contrast-default)">
                  {SUBTITLE}
                </Text>
              </S.Header>

              <S.GifWrapper ref={gifRef}>
                {hasEnteredView && (
                  <NextImage
                    src={SIMULATE_GIF}
                    alt="Demonstração de como simular o Crédito com Garantia de Imóvel"
                    fill
                    unoptimized
                    sizes="(max-width: 767px) 90vw, 34rem"
                  />
                )}
              </S.GifWrapper>

              <S.CtaWrapper>
                <Button color="primary" size="md" onClick={handleCtaClick}>
                  Simule agora
                </Button>
              </S.CtaWrapper>

              <S.BottomConnector aria-hidden />
            </S.Container>
          </S.Content>
        </Col>
      </Grid>
    </S.Simulation>
  )
})

Simulation.displayName = 'Simulation'

export default Simulation
